import { formatSettingsDate } from "./settings-shell.js";

export function formatMoney(amountCents, currency) {
  if (!amountCents || !currency) {
    return null;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amountCents / 100);
}

export function formatDate(value) {
  return formatSettingsDate(value) || null;
}

export function formatBillingCycleLabel(billingCycle) {
  return billingCycle === "annual" ? "year" : "month";
}

export function normalizeBillingCycle(billingCycle) {
  return billingCycle === "annual" ? "annual" : "monthly";
}

export function formatOfferExpiry(value) {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

export function formatSubscriptionStatusLabel(subscription) {
  if (!subscription) {
    return "Free plan";
  }
  const normalizedStatus = String(subscription.status || "").trim().toLowerCase();
  if (!normalizedStatus || normalizedStatus === "active") {
    return subscription.cancelAtPeriodEnd ? "Ending after this cycle" : "Auto-renewing";
  }
  return normalizedStatus
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function buildCheckoutPriceCatalog(plans = []) {
  return plans.reduce((accumulator, plan) => {
    const planCode = String(plan?.code || "").trim();
    if (!planCode) {
      return accumulator;
    }
    const planPrices = (plan?.prices || []).reduce((cycleAccumulator, price) => {
      const priceId = String(price?.id || "").trim();
      if (!priceId) {
        return cycleAccumulator;
      }
      const billingCycle = normalizeBillingCycle(price?.billingCycle);
      cycleAccumulator[billingCycle] = {
        id: priceId,
        billingCycle,
        amountCents: Number(price?.amountCents || 0),
        recurringAmountCents: Number(price?.recurringAmountCents || price?.amountCents || 0),
        currency: String(price?.currency || "").trim() || null,
      };
      return cycleAccumulator;
    }, {});
    if (Object.keys(planPrices).length > 0) {
      accumulator[planCode] = planPrices;
    }
    return accumulator;
  }, {});
}

export function mergeCheckoutPriceCatalogs(baseCatalog = {}, overrideCatalog = {}) {
  const merged = { ...baseCatalog };
  for (const [planCode, planCatalog] of Object.entries(overrideCatalog)) {
    merged[planCode] = {
      ...(merged[planCode] || {}),
      ...(planCatalog || {}),
    };
  }
  return merged;
}

export function resolveCheckoutPrice(planCatalog = {}, billingCycle = "monthly") {
  const normalizedBillingCycle = normalizeBillingCycle(billingCycle);
  return planCatalog?.[normalizedBillingCycle] || planCatalog?.monthly || planCatalog?.annual || null;
}

export function hasAnnualCheckoutOption(priceCatalog = {}) {
  return Object.values(priceCatalog).some((planCatalog) => Boolean(planCatalog?.annual));
}

export function formatCheckoutOptionLabel(price) {
  if (!price) {
    return null;
  }
  const amountLabel = formatMoney(price.amountCents, price.currency);
  if (!amountLabel) {
    return null;
  }
  return `${amountLabel} / ${formatBillingCycleLabel(price.billingCycle)}`;
}

export function toBillingSummaryPayload(payload = null) {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  return {
    accountId: payload.accountId || null,
    billingAccountId: payload.billingAccountId || null,
    currentPlan: payload.currentPlan || "free",
    subscription: payload.subscription || null,
    billingOwnerDisplayName: payload.billingOwnerDisplayName || null,
    billingOwnerPhoneNumber: payload.billingOwnerPhoneNumber || null,
    timing: payload.timing || null,
    freeExtrasGift: payload.freeExtrasGift || null,
  };
}
